import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { flatMap, map, take, concatMap, finalize, catchError } from 'rxjs/operators'
import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { AppRoutingConfig } from '../configuration/app.routing.config'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { AuditOrderData, KeySystem, OrderDetails, PaginatedList, Structure } from '../models/dev.test.models'
import { ErrorService } from './error.service'
import { KeyingDataSetsService } from './keying.data.sets.service'
import { MasterKeyService } from './keying.masterkey.service'
import { KeyingSystemService } from './keying.system.service'
import { LoadingService } from './loading.service'
import { KeyingCylinderService } from './keying.cylinder.service'
import { LoggingService } from './logging.service'
import { Accept } from './reports.service'
import { TokenizedMessageLoadingService } from './tokenized-message-loading/tokenized-message-loading.service'

export enum ReportName {
  CanadaReport = 'Canada Report',
  DesignReport = 'Keysystem Design Report',
  OverviewReport = 'Overview Report',
  BittingReport = 'Bittings Report',
  PinningChart = 'Pinning Chart Report',
  PinningChartSL = 'Pinning Chart SL Report',
  SiteMasterReport = 'SiteMaster Export ',
  ICOMMasterKeyReport = 'ICOM Master Key Report',
  ICOMChangeKeyReport = 'ICOM Change Key Report',
  OrderDetailReport = 'Order Detail Report',
}

@Injectable({
  providedIn: 'root',
})
export class KeyingStructureService {
  structureId = new BehaviorSubject<number>(null)
  structureId$ = this.structureId.asObservable()

  structures = new BehaviorSubject<Structure[]>([])
  structures$ = this.structures.asObservable()

  structure = new BehaviorSubject<Structure>(null)
  structure$ = this.structure.asObservable()

  orderDetails = new BehaviorSubject<OrderDetails[]>(null)
  orderDetails$ = this.orderDetails.asObservable()

  masterKeyProfiles: Array<KeySystem> = []

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private loadingService: LoadingService,
    private keyingSystemService: KeyingSystemService,
    private router: Router,
    private errorService: ErrorService,
    private masterKeyService: MasterKeyService,
    private keyingDataSetsService: KeyingDataSetsService,
    private keyingCylinderService: KeyingCylinderService,
    private loggingService: LoggingService,
    private tokenizedMessageLoadingService: TokenizedMessageLoadingService
  ) {}

  getUserStructures() {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users/structures`
    return this.httpClient.get<any[]>(url)
  }

  exportStructuresCSV(stateCodes: string, includeDealerSpec: boolean): Observable<Blob> {
    const token = this.tokenizedMessageLoadingService.startLoading('Exporting Structures', 'export-structures-csv', 'medium')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures`

    const params = new HttpParams()
      .set('stateCodes', stateCodes)
      .set('includeDealerSpec', includeDealerSpec.toString())

    const headers = new HttpHeaders().set('Accept', 'application/allegion.keying.export.structures+csv')

    return this.httpClient.get(url, { headers, params, responseType: 'blob' }).pipe(
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token)),
      catchError((error) => {
        // When responseType is 'blob', error.error is a Blob
        // We need to read it as text to get the actual error message
        if (error.error instanceof Blob) {
          return new Promise<never>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              try {
                const errorObj = JSON.parse(reader.result as string)
                reject({ ...error, error: errorObj })
              } catch (e) {
                reject({ ...error, error: { message: reader.result } })
              }
            }
            reader.onerror = () => {
              reject(error)
            }
            reader.readAsText(error.error)
          })
        }
        return throwError(error)
      })
    )
  }

  getUserStructureData(params): Observable<PaginatedList<Structure> | null> {
    const headerDict = {
      Accept: 'application/json',
    }
    return this.getStructures(params, headerDict)
  }

  getStructuresByUserId(params) {
    let paramString = this.setUserSearchString(params)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users/structures?${paramString}`
    return this.httpClient.get<Structure[]>(url)
  }

  getKeysystemRegistryNumbers(structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/masterkeys`

    let headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.get.registry.number.data+json')

    return this.httpClient.get<any>(url, { headers })
  }

  getStructureOrderHistory(params, structureId, keySystemId): Observable<PaginatedList<AuditOrderData> | null> {
    const headerDict = {
      Accept: 'application/json',
    }
    return this.getOrderHistory(structureId, keySystemId, params, headerDict)
  }

  getInbound(inbound) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Inbounds/${inbound}`
    return this.httpClient.get<any[]>(url)
  }

  getOutbound(outbound) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Outbounds/${outbound}`
    return this.httpClient.get<any[]>(url)
  }

  getStructureOrderDetail(structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Orders`
    //const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${keySystemId}/Orders`
    return this.httpClient.get<OrderDetails[]>(url)
  }

  getOrderSubLineDetails(structureId, keySystemId, orderId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Orders/${orderId}/`
    //const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${keySystemId}/Orders/${orderId}/`
    return this.httpClient.get<OrderDetails>(url)
  }

  private setUserSearchString(params) {
    let returnStr = ''
    for (let i = 0; i < params.length; i++) {
      returnStr += 'userids=' + params[i].userids
      if (i < params.length - 1) {
        returnStr += '&'
      }
    }
    return returnStr
  }

  mapStructuresByUserId(params, isSilent) {
    if (!isSilent) {
      this.tokenizedMessageLoadingService.startLoading('Getting Structures', 'structures-by-userid')
    }

    this.getStructuresByUserId(params)
      .pipe(take(1))
      .subscribe(
        (structures) => {
          if (!isSilent && structures.length === 0) {
            this.tokenizedMessageLoadingService.stopLoading('structures-by-userid')
          }
          structures.map((structure) => {
            structure.lockedById = structure.keySystems[0].lockedById
            structure.lockedBy = structure.keySystems[0].lockedBy
          })
          this.setStructures(structures)

          this.structure$.pipe(take(1)).subscribe((structure) => {
            if (structure) {
              this.keyingSystemService.setKeySystems(structure.keySystems)
            }
            // Kanban board owns stopping the structures-by-userid token after rendering non-empty results
          })
        },
        (error) => {
          if (!isSilent) {
            this.tokenizedMessageLoadingService.stopLoading('structures-by-userid')
          }
          this.errorService.throw(error)
        }
      )
  }

  private getOrderHistory(
    structureId,
    keySystemId,
    params: { [key: string]: any },

    headerDict: { Accept: string }
  ): Observable<PaginatedList<AuditOrderData>> {
    const searchParams = new URLSearchParams()
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] != null) {
        searchParams.append(key, params[key])
      }
    }

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    }

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/KeySystems/${keySystemId}/AuditTrail?${searchParams}`

    return this.httpClient
      .get<PaginatedList<AuditOrderData>>(
        `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/KeySystems/${keySystemId}/AuditTrail?${searchParams}`,
        requestOptions
      )
      .pipe(
        map((accounts) => {
          return {
            pageIndex: accounts.pageIndex,
            totalPages: accounts.totalPages,
            totalItems: accounts.totalItems,
            startItem: accounts.startItem,
            endItem: accounts.endItem,
            items: accounts.items.map((x) => {
              return Object.assign(new AuditOrderData(), {
                orderNumber: x.orderNumber,
                userName: x.userName,
                keyway: x.keyway,
                keySetSymbol: x.keySetSymbol,
                operatedBy: x.operatedBy,
                variation: x.variation,
                newValue: x.newValue,
                rpa: x.rpa,
                status: x.status,
                date: x.date,
                tmkName: x.tmkName,
                actionType: x.actionType,
                actionDescription: x.actionDescription,
                originalOrderNumber: x.originalOrderNumber,
              })
            }),
          }
        })
      )
  }

  private getStructures(
    params: { [key: string]: any },
    headerDict: { Accept: string }
  ): Observable<PaginatedList<Structure>> {
    const searchParams = new URLSearchParams()
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] != null) {
        searchParams.append(key, params[key])
      }
    }

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    }

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users/Structures/Search?${searchParams}`

    return this.httpClient
      .get<PaginatedList<Structure>>(
        `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users/Structures/Search?${searchParams}`,
        requestOptions
      )
      .pipe(
        map((accounts) => {
          return {
            pageIndex: accounts.pageIndex,
            totalPages: accounts.totalPages,
            totalItems: accounts.totalItems,
            startItem: accounts.startItem,
            endItem: accounts.endItem,
            items: accounts.items.map((x) => {
              return Object.assign(new Structure(), {
                name: x.name,
                id: x.id,
                structureNumber: x.structureNumber,
                city: x.city,
                state: x.stateCode,
                country: x.countryCode,
                hasDocuments: x.hasDocuments,
                TopMasterKeyName: x.keySystems[0]?.topMasterKeyName,
                TopMasterKeySection: x.keySystems[0]?.topMasterKeySection,
                keywayFamily: x.keywayFamily,
                keywayFamilyId: x.keywayFamilyId,
                combinatingRuleSetId: x.combinatingRuleSetId,
                pinCount: x.chamberCount,
                chamberTypeId: x.chamberTypeId,
                createdBy: x.createdBy,
                structureId: x.structureId,
                keySystems: x.keySystems,
                status: x.status,
                keyName: x.keyName,
              })
            }),
          }
        })
      )
  }

  getStructure(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`

    const headers = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.get.structure.with.keysystems+json'
    )

    return this.httpClient.get<Structure>(url, { headers })
  }

  getStructureOnboardStatus(structureId: number): Observable<boolean> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/onBoard`
    return this.httpClient.get<boolean>(url)
  }

  /* istanbul ignore next */
  deleteStructure(structureId, auditRequestFormData) {
    const type = 'application/json'

    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`
    return this.httpClient.delete<any>(url, { headers: headers, body: { auditTrailRequest: auditRequestFormData } })
  }

  getStructureKeySystems(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems`

    let headers = new HttpHeaders().set('Accept', 'application/allegion.keying.get.independent.keysystems+json')

    return this.httpClient.get<any>(url, { headers })
  }

  postUserStructure(structure) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Users/structures`
    return this.httpClient.post<any>(url, structure)
  }

  patchStructure(structureId, request) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`

    let headers = new HttpHeaders().set('Content-Type', 'application/allegion.structure.update+json')

    return this.httpClient.patch<any>(url, request, { headers })
  }

  postStructureMasterKeys(options, structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/masterKeys`
    return this.httpClient.post<any>(url, options)
  }

  setStructures(structures) {
    this.loggingService.log('Set Structures', structures)
    this.structures.next(structures)
  }

  setOrderDetails(orderDetails) {
    this.orderDetails.next(orderDetails)
  }

  setStructureId(structureId) {
    this.structureId.next(structureId)
  }

  selectStructure(structure: Structure) {
    this.structure.next(structure)
  }

  rejectOrder(structureId: number, orderId: number, DeleteOrderData: boolean) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Orders/Reject`
    let type
    type = 'application/json'
    const body = {
      orderId: orderId,
      DeleteOrderData: DeleteOrderData,
    }

    let headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.put<any>(url, body, { headers })
  }

  setStructureById(id) {
    this.loggingService.log('Set Structure by ID:', id)
    this.structures$.pipe(take(1)).subscribe((structures: Structure[]) => {
      structures.forEach((structure) => {
        if (structure.id == id) {
          this.loggingService.log('Structure Found!', structure)
          this.selectStructure(structure)
        }
      })
    })
  }

  loadMappedStructures() {
    this.loadingService.set({ isLoading: true, message: 'Getting Structures' })

    this.getUserStructures()
      .pipe(take(1))
      .subscribe(
        (structures) => {
          structures.map((structure) => {
            structure.lockedById = structure.keySystems[0].lockedById
            structure.lockedBy = structure.keySystems[0].lockedBy
          })
          this.setStructures(structures)

          this.structure$.pipe(take(1)).subscribe((structure) => {
            if (structure) {
              this.keyingSystemService.setKeySystems(structure.keySystems)
            }
            this.loadingService.set({ isLoading: false, message: '' })
          })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  createKeySystem(data) {
    this.loadingService.set({ isLoading: true, message: 'Creating Master Key System' })
    this.loggingService.log(`Creating for structure:`, data.structure)
    let newKeySystemRes

    // Create Structure
    this.keyingSystemService
      .createNewKeySystem(data.keySystem, data.structure.id)
      .pipe(
        take(1),
        flatMap((nksRes) => {
          this.loggingService.log('New Key System Created:', nksRes)
          this.loadingService.set({ isLoading: true, message: 'Creating Structure Master Keys' })
          this.loadingService.set({ isLoading: false, message: '' })
          newKeySystemRes = nksRes

          // Create KeySystem ( Profile )
          return this.masterKeyService.postMasterKeys(data.masterKeys, data.structure.id, newKeySystemRes.id)
        })
      )
      .subscribe(
        () => {
          this.loadMappedStructures()

          // Nav to Structure
          this.structure$.pipe(take(1)).subscribe((selectedStructure: Structure) => {
            if (selectedStructure) {
              this.keyingSystemService
                .getKeySystems(selectedStructure.id)
                .pipe(take(1))
                .subscribe((keySystems) => {
                  selectedStructure.keySystems = keySystems
                  this.selectStructure(selectedStructure)
                  this.router.navigate([
                    AppRoutingConfig.getStructureBudgetUrl(selectedStructure.id, newKeySystemRes.id),
                  ])
                  this.keyingDataSetsService.loadKeySystemDataSets(selectedStructure.id, newKeySystemRes.id)
                })
            }
            this.loadingService.set({ isLoading: false, message: '' })
          })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  createStructure(data) {
    this.loadingService.set({ isLoading: true, message: 'Creating New Structure' })
    this.loggingService.log(`Creating structure:`, data.structure)
    let newStructureRes
    let newKeySystemRes

    // Create Structure
    this.postUserStructure(data.structure)
      .pipe(
        take(1),
        flatMap((sRes: Structure) => {
          this.loggingService.log('New Structure Created:', sRes)
          this.loadingService.set({ isLoading: true, message: 'Creating Master Key System' })
          this.loadingService.set({ isLoading: false, message: '' })
          newStructureRes = sRes

          // Create KeySystem ( Profile )
          return this.keyingSystemService.createNewKeySystem(data.keySystem, newStructureRes.id)
        })
      )
      .pipe(
        take(1),
        flatMap((nksRes: Structure) => {
          this.loggingService.log('New Key System Created:', nksRes)
          this.loadingService.set({ isLoading: true, message: 'Creating Structure Master Keys' })
          this.loadingService.set({ isLoading: false, message: '' })
          newKeySystemRes = nksRes

          // Create KeySystem MasterKeys
          return this.masterKeyService.postMasterKeys(data.masterKeys, newStructureRes.id, newKeySystemRes.id)
        })
      )
      .subscribe(
        () => {
          this.loadMappedStructures()

          // Select new Structure
          this.selectStructure(newStructureRes)

          // Select New Key System
          this.keyingSystemService.selectKeySystem(newKeySystemRes)

          // Nav to Structure
          this.structure$.pipe(take(1)).subscribe((selectedStructure: Structure) => {
            if (selectedStructure) {
              if (data.keySystem.isDealerKeySystem) {
                this.router.navigate([AppRoutingConfig.getDealerKeySystemUrl(selectedStructure.id, newKeySystemRes.id)])
              } else {
                this.router.navigate([AppRoutingConfig.getStructureUrl(selectedStructure.id, newKeySystemRes.id)])
              }
            }
            this.loadingService.set({ isLoading: false, message: '' })
          })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  updateStructure(data) {
    this.loadingService.set({ isLoading: true, message: 'Creating New Structure' })

    const structureRequest = [
      {
        op: 'replace',
        path: '/Name',
        value: data.structure.name,
      },
      {
        op: 'replace',
        path: '/Address',
        value: {
          city: data.structure.city,
          stateCode: data.structure.stateCode,
          countryCode: data.structure.countryCode,
        },
      },
      {
        op: 'replace',
        path: '/CombinatingRuleSetId',
        value: data.structure.combinatingRuleSetId,
      },
      {
        op: 'replace',
        path: '/HasDocuments',
        value: data.structure.hasDocuments,
      },
    ]

    this.patchStructure(data.structure.id, structureRequest)
      .pipe(
        take(1),
        flatMap(() => {
          this.loadingService.set({ isLoading: true, message: 'Creating Master Key System' })
          this.loadingService.set({ isLoading: false, message: '' })
          const keySystemRequest = [
            {
              op: 'replace',
              path: '/IsDealerKeySystem',
              value: data.keySystem.isDealerKeySystem,
            },
            {
              op: 'replace',
              path: '/ConstructionTypeId',
              value: data.keySystem.constructionTypeId,
            },
            {
              op: 'replace',
              path: '/HasDocuments',
              value: data.structure.hasDocuments,
            },
          ]

          return this.keyingSystemService.patchKeySystem(data.structure.id, data.keySystem.id, keySystemRequest).pipe(
            concatMap(() => {
              this.loadingService.set({ isLoading: true, message: 'Creating Cylinders With Wafer Deactivate' })
              return this.keyingCylinderService.createDeactCylinders(data.structure.id, data.keySystem.id).pipe(
                finalize(() => {
                  this.loadingService.set({ isLoading: false, message: '' })
                })
              )
            })
          )
        })
      )
      .subscribe(
        () => {
          // Nav to Structure
          this.structure$.pipe(take(1)).subscribe((selectedStructure: Structure) => {
            if (selectedStructure) {
              if (data.keySystem.isDealerKeySystem) {
                this.router.navigate([AppRoutingConfig.getDealerKeySystemUrl(selectedStructure.id, data.keySystem.id)])
              } else {
                this.router.navigate([AppRoutingConfig.getStructureUrl(selectedStructure.id, data.keySystem.id)])
              }
            }
            this.loadingService.set({ isLoading: false, message: '' })
          })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  getAttachments(structureId) {
    this.loadingService.set({ isLoading: true, message: 'Getting Attachments' })
    let headers
    headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/allegion.keying.structure.attachment+json',
      }),
    }
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents`
    return this.httpClient.get<any>(url, { headers })
  }

  uploadAttachment(structureId, files) {
    let fileToUpload = <File>files[0]
    const formData = new FormData()
    formData.append('formFile', fileToUpload, fileToUpload.name)
    let headers
    headers = {
      headers: new HttpHeaders({
        'Content-Type': 'application/octet-stream',
      }),
    }

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents`
    return this.httpClient.post<any>(url, formData, { headers })
  }

  deleteAttachment(structureId, attachmentId) {
    let headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.delete.structure.attachment+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents/${attachmentId}`
    return this.httpClient.delete<any>(url, { headers })
  }

  downloadAttachment(structureId, attachmentId) {
    let headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.structure.attachment+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Documents/${attachmentId}`
    return this.httpClient.get(url, { headers, responseType: 'blob' })
  }

  getReport(structureId, KeySystemId, accept: Accept, isRequeue): Observable<Blob> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}/Reports/${isRequeue}`

    const headers = new HttpHeaders().set('Accept', accept)

    return this.httpClient.get(url, { headers, responseType: 'blob' })
  }

  /* istanbul ignore next */
  submitFullReportRequest(structureId, KeySystemId, childHttpRoute, acceptHeader, isRequeue: boolean) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}/Reports/${childHttpRoute}`
    // Create headers
    const headers = new HttpHeaders().set('Accept', acceptHeader)
    // Create query parameters
    const params = new HttpParams().set('isRequeue', isRequeue)

    return this.httpClient.post<any>(url, null, { headers, params })
  }

  /* istanbul ignore next */
  submitPartialReportRequest(structureId, KeySystemId, childHttpRoute, acceptHeader, payload) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}/Reports/${childHttpRoute}`

    const headers = new HttpHeaders().set('Accept', acceptHeader)

    return this.httpClient.post<any>(url, payload, { headers })
  }

  getOrderDetailsByStructureId(structureId, keySystemId) {
    // this.orderDetails$.pipe(take(1)).subscribe((orderDetails: OrderDetails[]) => {
    //   this.setOrderDetails(orderDetails)
    // })

    this.getStructureOrderDetail(structureId, keySystemId)
      .pipe(take(1))
      .subscribe((result: OrderDetails[]) => {
        this.setOrderDetails(result)
      })
  }

  /* istanbul ignore next */
  upgradeStructure(structureId) {
    const contentType = 'application/json'
    const headers = new HttpHeaders().set('Content-Type', contentType)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/upgrade`
    return this.httpClient.put<any>(url, { headers: headers })
  }
}
